import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material'
import Form from '../../../components/forms/Form'
import SimpleBody from '../../../components/forms/SimpleBody'
import useBaseService from '../../../hooks/services/useBaseService'
import useForm from '../../../hooks/useForm'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import useNotification from '../../../hooks/useNotification'
import { entities } from '../../../constants/entities'
import { groupFields } from '../../../constants/forms/almoxarifado/groupFields'
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../utils/objectUtils'

/**
 * Cria um grupo sem sair da tela atual.
 *
 * Reaproveita os mesmos campos/validações da página de cadastro de grupo
 * (groupFields + POST /groups). Ao criar com sucesso, devolve o grupo criado
 * via onCreated para o chamador já selecioná-lo.
 */
export default function CreateGroupModal({ open, onClose, onCreated }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showNotification } = useNotification()
  const {
    post: baseServicePost,
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)

  const [groupSection] = groupFields

  const { formData, handleChange, resetForm, setFormData } = useForm(
    setDefaultFieldValues([groupSection]),
    [groupSection]
  )

  const typeSelect = useRemoteSelectOptions({
    queryKey: ['groupTypeOptions'],
    columnDataBase: ['id', 'name'],
    baseServiceGet,
    entityPath: entities.types.get,
    fieldId: 'group.types',
    formData,
    isMulti: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id}- ${item.name}`,
    }),
    setFormData,
    perPage: 15,
    enablePagination: true,
  })

  const selects = useMemo(
    () => ({
      'group.types': typeSelect,
    }),
    [typeSelect]
  )

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects]
  )

  const createGroupMutation = useMutation({
    mutationFn: async (data) => baseServicePost(entities.groups.create, data),
    onSuccess: (response) => {
      if (response?.status === 201) {
        showNotification('success', 'Grupo criado com sucesso.')
        queryClient.invalidateQueries({ queryKey: ['upgradeApprovalGroups'] })
        queryClient.invalidateQueries({ queryKey: ['almox_groups'] })
        onCreated?.(response.result)
        handleClose()
      }
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
    },
  })

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedGroup = cleanPayload(formData)
      const typesIds = (formData.group?.types || []).map((type) =>
        typeof type === 'object' && type !== null ? type.value : type
      )

      await createGroupMutation.mutateAsync({
        group: {
          ...cleanedGroup.group,
          types: typesIds,
        },
      })
    } catch (error) {
      console.error('Erro ao criar grupo:', {
        status: error.status,
        message: error.message,
      })
    }
  }, [formData, createGroupMutation])

  const handleClose = useCallback(() => {
    onClose?.()
    resetForm()
    setFormErrors({})
  }, [onClose, resetForm, setFormErrors])

  const normalizedFormErrors = normalizeFormErrors(formErrors)

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>Cadastrar novo grupo</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Form
            initialFormData={{ ...formData }}
            onSubmit={handleSubmit}
            textSubmit="Cadastrar Grupo"
            textLoadingSubmit="Cadastrando..."
            showButtonBack={false}>
            {() => (
              <SimpleBody
                fields={groupSection.fields}
                formErrors={normalizedFormErrors}
                formData={formData}
                handleFieldChange={handleChange}
                getOptions={getOptions}
                getSelectedValue={getSelectedValue}
                getSelectMeta={getSelectMeta}
              />
            )}
          </Form>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
